import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import { installAuthRefresh } from '../lib/authRefresh';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Install global silent-refresh on first import (covers fetch + axios)
installAuthRefresh(axiosInstance);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Listen for 'session-expired' broadcasts from authRefresh interceptor.
  // When the refresh_token cookie is also expired, every API 401 leaves the
  // user stuck on the current page with a confusing "HTTP 401" toast. Here we
  // surface a friendly toast and reset the user so AuthContext redirects to /login.
  useEffect(() => {
    let lastShownAt = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastShownAt < 4000) return; // throttle
      lastShownAt = now;
      try {
        // dynamic import to avoid bundling sonner at the top of AuthContext
        // (it's already loaded elsewhere)
        import('sonner').then(({ toast }) => {
          toast.error('Sessione scaduta. Effettua nuovamente il login per continuare.', {
            duration: 6000,
          });
        }).catch(() => { /* noop */ });
      } catch (e) { /* noop */ }
      setUser(false);
    };
    window.addEventListener('app:session-expired', handler);
    return () => window.removeEventListener('app:session-expired', handler);
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axiosInstance.get('/auth/me');
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axiosInstance.post('/auth/login', { email, password });
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const register = async (email, password, name, company_id) => {
    try {
      const { data } = await axiosInstance.post('/auth/register', {
        email,
        password,
        name,
        company_id,
      });
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
      setUser(false);
    } catch {
      setUser(false);
    }
  };

  const contextValue = useMemo(() => ({
    user, loading, login, register, logout, axiosInstance
  }), [user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;